import React, { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';

// Define the state interface
export interface WebcamSourceConfigState {
  webcamDeviceId: string | null;
  cropLeft: number;
  cropRight: number;
  cropTop: number;
  cropBottom: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  sensitivity: number; // 0 to 100
}

// LocalStorage key constant
const STORAGE_KEY = 'webcam-source-config-v1';

// Functions to handle localStorage
function loadStateFromStorage(): WebcamSourceConfigState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate that the parsed object has the expected structure
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof parsed.cropLeft === 'number' &&
        typeof parsed.cropRight === 'number' &&
        typeof parsed.cropTop === 'number' &&
        typeof parsed.cropBottom === 'number' &&
        typeof parsed.flipHorizontal === 'boolean' &&
        typeof parsed.flipVertical === 'boolean' &&
        typeof parsed.sensitivity === 'number' &&
        (parsed.webcamDeviceId === null || typeof parsed.webcamDeviceId === 'string')
      ) {
        return parsed as WebcamSourceConfigState;
      }
    }
  } catch (error) {
    console.warn('Failed to load webcam config from localStorage:', error);
  }
  return null;
}

function saveStateToStorage(state: WebcamSourceConfigState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save webcam config to localStorage:', error);
  }
}

// Define action types
export type WebcamSourceConfigAction =
  | { type: 'SET_WEBCAM_DEVICE_ID'; payload: string | null }
  | { type: 'SET_CROP_LEFT'; payload: number }
  | { type: 'SET_CROP_RIGHT'; payload: number }
  | { type: 'SET_CROP_TOP'; payload: number }
  | { type: 'SET_CROP_BOTTOM'; payload: number }
  | { type: 'SET_FLIP_HORIZONTAL'; payload: boolean }
  | { type: 'SET_FLIP_VERTICAL'; payload: boolean }
  | { type: 'SET_SENSITIVITY'; payload: number }
  | { type: 'RESET_CONFIG' };

// Initial state
const defaultState: WebcamSourceConfigState = {
  webcamDeviceId: null,
  cropLeft: 0,
  cropRight: 0,
  cropTop: 0,
  cropBottom: 0,
  flipHorizontal: false,
  flipVertical: false,
  sensitivity: 50, // Default to middle value
};

// Get initial state from localStorage or use default
const getInitialState = (): WebcamSourceConfigState => {
  const storedState = loadStateFromStorage();
  return storedState || defaultState;
};

// Reducer function
function webcamSourceConfigReducer(
  state: WebcamSourceConfigState,
  action: WebcamSourceConfigAction
): WebcamSourceConfigState {
  switch (action.type) {
    case 'SET_WEBCAM_DEVICE_ID':
      return { ...state, webcamDeviceId: action.payload };
    case 'SET_CROP_LEFT':
      return { ...state, cropLeft: Math.max(0, action.payload) };
    case 'SET_CROP_RIGHT':
      return { ...state, cropRight: Math.max(0, action.payload) };
    case 'SET_CROP_TOP':
      return { ...state, cropTop: Math.max(0, action.payload) };
    case 'SET_CROP_BOTTOM':
      return { ...state, cropBottom: Math.max(0, action.payload) };
    case 'SET_FLIP_HORIZONTAL':
      return { ...state, flipHorizontal: action.payload };
    case 'SET_FLIP_VERTICAL':
      return { ...state, flipVertical: action.payload };
    case 'SET_SENSITIVITY':
      return { 
        ...state, 
        sensitivity: Math.max(0, Math.min(100, action.payload)) // Clamp between 0-100
      };
    case 'RESET_CONFIG':
      return defaultState;
    default:
      return state;
  }
}

// Wrapper reducer that handles localStorage persistence
function persistedWebcamSourceConfigReducer(
  state: WebcamSourceConfigState,
  action: WebcamSourceConfigAction
): WebcamSourceConfigState {
  const newState = webcamSourceConfigReducer(state, action);
  
  // Save to localStorage after each state change
  saveStateToStorage(newState);
  
  return newState;
}

// Context interface
export interface WebcamSourceConfigContextType {
  state: WebcamSourceConfigState;
  dispatch: React.Dispatch<WebcamSourceConfigAction>;
  // Convenience methods
  setWebcamDeviceId: (deviceId: string | null) => void;
  setCropLeft: (value: number) => void;
  setCropRight: (value: number) => void;
  setCropTop: (value: number) => void;
  setCropBottom: (value: number) => void;
  setFlipHorizontal: (value: boolean) => void;
  setFlipVertical: (value: boolean) => void;
  setSensitivity: (value: number) => void;
  resetConfig: () => void;
}

// Create the context
const WebcamSourceConfigContext = createContext<WebcamSourceConfigContextType | undefined>(undefined);

// Provider component props
interface WebcamSourceConfigProviderProps {
  children: ReactNode;
}

// Provider component
export function WebcamSourceConfigProvider({ children }: WebcamSourceConfigProviderProps) {
  const [state, dispatch] = useReducer(persistedWebcamSourceConfigReducer, getInitialState());

  // Convenience methods
  const setWebcamDeviceId = (deviceId: string | null) => {
    dispatch({ type: 'SET_WEBCAM_DEVICE_ID', payload: deviceId });
  };

  const setCropLeft = (value: number) => {
    dispatch({ type: 'SET_CROP_LEFT', payload: value });
  };

  const setCropRight = (value: number) => {
    dispatch({ type: 'SET_CROP_RIGHT', payload: value });
  };

  const setCropTop = (value: number) => {
    dispatch({ type: 'SET_CROP_TOP', payload: value });
  };

  const setCropBottom = (value: number) => {
    dispatch({ type: 'SET_CROP_BOTTOM', payload: value });
  };

  const setFlipHorizontal = (value: boolean) => {
    dispatch({ type: 'SET_FLIP_HORIZONTAL', payload: value });
  };

  const setFlipVertical = (value: boolean) => {
    dispatch({ type: 'SET_FLIP_VERTICAL', payload: value });
  };

  const setSensitivity = (value: number) => {
    dispatch({ type: 'SET_SENSITIVITY', payload: value });
  };

  const resetConfig = () => {
    dispatch({ type: 'RESET_CONFIG' });
  };

  const contextValue: WebcamSourceConfigContextType = {
    state,
    dispatch,
    setWebcamDeviceId,
    setCropLeft,
    setCropRight,
    setCropTop,
    setCropBottom,
    setFlipHorizontal,
    setFlipVertical,
    setSensitivity,
    resetConfig,
  };

  return (
    <WebcamSourceConfigContext.Provider value={contextValue}>
      {children}
    </WebcamSourceConfigContext.Provider>
  );
}

// Custom hook to use the context
export function useWebcamSourceConfig(): WebcamSourceConfigContextType {
  const context = useContext(WebcamSourceConfigContext);
  if (context === undefined) {
    throw new Error('useWebcamSourceConfig must be used within a WebcamSourceConfigProvider');
  }
  return context;
}
